
import { mapState } from "vuex";

import { colors } from "~/colors.config";

export default {
  data: () => ({
    disclaimers: {
      size: "The bed shown is a queen size bed. Explore other sizes in-store to suit your space.",
      colours:
        "We've done our best to match colours, finishes and stains, but colours are affected by device settings so it is very important to check against real samples in store.",
      construction:
        "Every bedframe and headboard is custom made in Australia by craftsmen. The computer-generated image may not exactly represent construction details. If you have any questions, please ask in store.",
    },
  }),

  computed: {
    ...mapState("bedbuilder", [
      "activeOptions",
      "activeImagesBase64",
      "headerBase64",
    ]),

    mattressImage() {
      const materialImages = {
        Timber: this.activeImagesBase64.timberMattress,
        Upholstered: this.activeImagesBase64.upholsteredMattress,
      };
      return materialImages[this.activeOptions.Material] || "";
    },
  },

  methods: {
    async generateCanvas() {
      const element = this.$refs.pdfContent;

      const originalStyles = this.storeElementStyles(element);
      this.prepareElementForRendering(element);

      try {
        const html2canvas = (await import("html2canvas")).default;
        await Promise.all([document.fonts.ready, this.waitForImages(element)]);
        const canvas = await html2canvas(element, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: colors.gray[100],
          logging: false,
          width: 794,
          height: 1123,
          windowWidth: 794,
          windowHeight: 1123,
        });

        this.restoreElementStyles(element, originalStyles);
        return canvas;
      } catch (error) {
        this.restoreElementStyles(element, originalStyles);
        throw error;
      }
    },
    storeElementStyles(element) {
      return {
        position: element.style.position,
        left: element.style.left,
        opacity: element.style.opacity,
        pointerEvents: element.style.pointerEvents,
        zIndex: element.style.zIndex,
        transform: element.style.transform,
      };
    },
    prepareElementForRendering(element) {
      element.setAttribute(
        "style",
        [
          "position: absolute",
          "left: 0",
          "top: 0",
          "opacity: 1",
          "pointer-events: none",
          "z-index: -1000",
          "transform: translateX(-9999px)",
        ].join("; ")
      );
    },
    restoreElementStyles(element, originalStyles) {
      Object.keys(originalStyles).forEach(key => {
        element.style[key] = originalStyles[key];
      });
    },
    waitForImages(element) {
      const images = element.querySelectorAll("img");
      const promises = Array.from(images).map(img => {
        if (img.complete) return Promise.resolve();
        return new Promise(resolve => {
          img.onload = resolve;
          img.onerror = resolve;
        });
      });
      return Promise.all(promises);
    },
    async downloadPDF() {
      this.$store.dispatch("bedbuilder/setPDFLoading", true);

      try {
        const canvas = await this.generateCanvas();
        const { jsPDF } = await import("jspdf");

        // eslint-disable-next-line new-cap
        const pdf = new jsPDF({
          orientation: "portrait",
          unit: "mm",
          format: "a4",
        });

        const { imgWidth, imgHeight, xOffset, yOffset } =
          this.calculatePDFDimensions(canvas);

        pdf.addImage(
          canvas.toDataURL("image/png"),
          "PNG",
          xOffset,
          yOffset,
          imgWidth,
          imgHeight
        );

        const fileName = `snooze-bed-${this.activeOptions.Material.toLowerCase()}-${this.activeOptions.Colour.toLowerCase()}-${
          new Date().toISOString().split("T")[0]
        }.pdf`;

        pdf.link(105, 280, 75, 10, {
          url: "https://www.snooze.com.au/store/store-locator",
        });

        pdf.save(fileName);
      } catch (error) {
        this.$bugsnag.notify(error);
      } finally {
        this.$store.dispatch("bedbuilder/setPDFLoading", false);
      }
    },
    calculatePDFDimensions(canvas) {
      const pdfWidth = 210;
      const pdfHeight = 297;
      const ratio = Math.min(
        pdfWidth / (canvas.width * 0.264583),
        pdfHeight / (canvas.height * 0.264583)
      );
      const imgWidth = canvas.width * 0.264583 * ratio;
      const imgHeight = canvas.height * 0.264583 * ratio;
      const xOffset = (pdfWidth - imgWidth) / 2;

      return { imgWidth, imgHeight, xOffset, yOffset: 0 };
    },
    async printPreview() {
      this.isPrintPreview = true;

      try {
        const canvas = await this.generateCanvas();

        // Create print window for iOS compatibility
        const printWindow = window.open("", "_blank", "width=794,height=1123");
        if (!printWindow) {
          // Fallback to original method if popup blocked
          return this.fallbackPrint(canvas);
        }

        const printImage = document.createElement("img");
        printImage.src = canvas.toDataURL("image/png");

        const htmlContent = `<!DOCTYPE html>
          <html>
            <head>
              <title>Print Preview</title>
              <style>
                html, body {
                  margin: 0 !important;
                  padding: 0 !important;
                  height: 100vh !important;
                  width: 100vw !important;
                  overflow: hidden !important;
                  display: flex !important;
                  align-items: center !important;
                  justify-content: center !important;
                  background: white !important;
                }

                img {
                  max-width: 100% !important;
                  max-height: 100% !important;
                  width: auto !important;
                  height: auto !important;
                  object-fit: contain !important;
                  display: block !important;
                }

                @media print {
                  html, body {
                    margin: 0 !important;
                    padding: 0 !important;
                    width: 210mm !important;
                    height: 297mm !important;
                  }

                  img {
                    width: 210mm !important;
                    height: auto !important;
                    max-height: 297mm !important;
                    object-fit: contain !important;
                    page-break-inside: avoid !important;
                  }

                  @page {
                    size: A4 portrait;
                    margin: 0;
                  }
                }
              </style>
            </head>
            <body>
              <img src="${canvas.toDataURL("image/png")}" alt="Print Preview" />
            </body>
        </html>`;

        printWindow.document.write(htmlContent);

        printWindow.document.close();

        // Handle print and close after document loads
        printWindow.onload = function () {
          setTimeout(function () {
            printWindow.print();
            setTimeout(function () {
              printWindow.close();
            }, 100);
          }, 500);
        };
      } catch (error) {
        this.$bugsnag.notify(error);
        this.isPrintPreview = false;
      }
    },

    // Fallback method for when popup is blocked
    async fallbackPrint(canvas) {
      const printImage = document.createElement("img");
      printImage.src = canvas.toDataURL("image/png");
      printImage.style.cssText =
        "width: 100%; height: auto; max-width: 210mm; display: block; margin: 0 auto; page-break-inside: avoid;";

      this.addPrintStyles();

      await new Promise(resolve => {
        if (printImage.complete) {
          resolve();
        } else {
          printImage.onload = resolve;
          printImage.onerror = resolve;
        }
      });

      const vueApp =
        document.body.querySelector("#app") || document.body.firstElementChild;
      const originalDisplay = vueApp ? vueApp.style.display : "";

      if (vueApp) {
        vueApp.style.display = "none";
      }

      const printContainer = document.createElement("div");
      printContainer.className = "print-image-container";
      printContainer.appendChild(printImage);
      document.body.insertBefore(printContainer, document.body.firstChild);

      // eslint-disable-next-line no-unused-expressions
      document.body.offsetHeight;
      window.print();

      setTimeout(() => {
        if (printContainer.parentNode) {
          printContainer.parentNode.removeChild(printContainer);
        }
        if (vueApp) {
          vueApp.style.display = originalDisplay;
        }
        this.removePrintStyles();
        this.isPrintPreview = false;
      }, 100);
    },

    addPrintStyles() {
      if (document.getElementById("canvas-print-styles")) return;

      const styles = document.createElement("style");
      styles.id = "canvas-print-styles";
      styles.innerHTML = `
        @media print {
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            height: 297mm !important;
            width: 210mm !important;
            overflow: hidden !important;
          }

          body * {
            visibility: hidden !important;
          }

          .print-image-container,
          .print-image-container * {
            visibility: visible !important;
          }

          .print-image-container {
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            width: 210mm !important;
            height: 297mm !important;
            max-height: 297mm !important;
            max-width: 210mm !important;
            background: white !important;
            padding: 0 !important;
            margin: 0 !important;
            overflow: hidden !important;
            page-break-before: avoid !important;
            page-break-after: avoid !important;
            page-break-inside: avoid !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
          }

          .print-image-container img {
            display: block !important;
            width: 210mm !important;
            height: auto !important;
            max-height: 297mm !important;
            object-fit: contain !important;
            margin: 0 auto !important;
            page-break-inside: avoid !important;
            page-break-after: avoid !important;
          }

          @page {
            size: A4 portrait;
            margin: 0;
          }
        }
      `;
      document.head.appendChild(styles);
    },
    removePrintStyles() {
      const styleEl = document.getElementById("canvas-print-styles");
      if (styleEl?.parentNode) {
        styleEl.parentNode.removeChild(styleEl);
      }
    },
  },
};
